import { type Color, type IconName } from "@salestrak/ui";

// `kind` is a GraphQL enum, so its value set is declared ONCE server-side
// (apps/api/core/src/integrations/types.ts) and arrives here through codegen.
// Imported for use below AND re-exported, so the integration view types stay a
// single import and can never drift from the schema.
import type {
  IntegrationAuthFieldRow,
  IntegrationCategoryRow,
  IntegrationConnectionRow,
  IntegrationKind,
  IntegrationProviderRow,
} from "#app/lib/data";
export type { IntegrationKind };

// ---------------------------------------------------------------------------
// Integration hub view types + client-only helpers. DATA comes from the Core
// API (`query IntegrationCatalog` → `useIntegrationCatalog()` in
// ./use-integration-catalog.ts, assembled from the code provider registry —
// no DB). Each view type is DERIVED from its lib/data row alias — the
// intersections below only narrow the server's plain strings to the library's
// unions and normalize `null` to absence; they never restate a field.
//  - Providers: integration cards browsed by category; the left-nav categories
//    FILTER the provider cards.
// ---------------------------------------------------------------------------

/** Recursively swap `null` for `undefined` — the view types' optionals. */
type NullToUndefined<T> = T extends null
  ? undefined
  : T extends (infer U)[]
    ? NullToUndefined<U>[]
    : T extends object
      ? { [K in keyof T]: NullToUndefined<T[K]> }
      : T;

export type IntegrationCategory = Omit<
  NullToUndefined<IntegrationCategoryRow>,
  "icon" | "__typename"
> & {
  icon: IconName;
};

/** One credential input the connect form collects (API key, id, secret…). */
export type AuthField = Omit<
  NullToUndefined<IntegrationAuthFieldRow>,
  "control" | "__typename"
> & {
  /** Input control: "text" (default), "textarea" (multi-line), or "list"
   * (multi-entry add/remove editor). */
  control?: "text" | "textarea" | "list" | undefined;
};

/** A saved configuration of a provider in the workspace (multi-config). */
export type IntegrationConnection = Omit<
  NullToUndefined<IntegrationConnectionRow>,
  "__typename"
>;

/** A provider marketplace card, sourced from the code registry. */
export type IntegrationProvider = Omit<
  NullToUndefined<IntegrationProviderRow>,
  "icon" | "color" | "authFields" | "connections" | "__typename"
> & {
  icon: IconName;
  /** Brand accent color for the provider's icon. */
  color: Color;
  /** Full-color brand logo (client-bundled tile); when set the card renders it
   * instead of the registry `icon`, which stays as the fallback. */
  logo?: string | undefined;
  authFields: AuthField[];
  connections: IntegrationConnection[];
};

/**
 * A manual step the admin performs in the provider's own product for a
 * connection to work. Derived from
 * the provider view type rather than restated, so it cannot drift; `NonNullable`
 * because the consumer only renders it when the provider declares one.
 */
export type ConnectionGuide = NonNullable<IntegrationProvider["connectionGuide"]>;

/**
 * One collection an admin can browse on a connection's Features tab. Derived
 * from the provider view type rather than restated, so it cannot drift.
 */
export type RemoteCollection = IntegrationProvider["remoteCollections"][number];

/** Filter providers to a category, honoring the special buckets
 * (`featured` / `all-integrations`). `list` comes from `useIntegrationCatalog()`. */
export function filterProviders(
  categoryId: string,
  list: IntegrationProvider[],
): IntegrationProvider[] {
  if (categoryId === "featured") return list.filter((p) => p.featured);
  if (categoryId === "all-integrations") return list;
  return list.filter((p) => p.categoryIds.includes(categoryId));
}
